import React, { useState, useEffect, useRef, useCallback } from 'react';
// eslint-disable-next-line
import { useNavigate } from 'react-router-dom';
import './SpeechToText.css';
import env from '../utils/env';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
// eslint-disable-next-line
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment'; // Import payment icon
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { getApiCall } from '../utils/Apicalls';
// eslint-disable-next-line
import EditIcon from '@mui/icons-material/Edit';
import {io} from 'socket.io-client';

function SpeechToText({ onEndSession, sessionConfig, remainingTime }) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interView, setInterView] = useState('webApp');
  const [response, setResponse] = useState('');
  const [desktopToken, setDesktopToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copyToken, setCopyToken] = useState(false);
  const [tokenDetails, setTokenDetails] = useState(env.OPENAI_API_KEY);
  const [token2Details, setToken2Details] = useState(env.OPENAI_API_KEY);

  const [socketObj, setSocketObj] = useState(null);
  const [userId, setUserId] = useState(null);

  const [exitDialog, setExitDialog] = useState(false);
  
  // Session context state - use sessionConfig directly with job title and interview details
  const [currentSessionConfig, setCurrentSessionConfig] = useState(sessionConfig || {
    interViewTime: 0,
    role: 'Software Engineer',
    skills: ['JavaScript', 'React', 'Node.js'],
    interviewType: 'technical',
    jobTitle: '',
    jobDescription: ''
  });
   // eslint-disable-next-line
  const [showContextDialog, setShowContextDialog] = useState(false);
   // eslint-disable-next-line
  const [editedSessionConfig, setEditedSessionConfig] = useState({});
   // eslint-disable-next-line
  const [skillInput, setSkillInput] = useState('');


  React.useEffect(() => {
    const interViewType = localStorage.getItem('interView');
    if (interViewType) {
      setInterView(interViewType);
    } 
    const getDesktopToken = localStorage.getItem('desktopToken');
    if (getDesktopToken) {
      setDesktopToken(getDesktopToken);
    }

    if (process.env.REACT_APP_CONNECT_SOCKET === 'true' && process.env.REACT_APP_SOCKET_BACKEND_URL) {
      const socket = io(process.env.REACT_APP_SOCKET_BACKEND_URL);
      setSocketObj(socket);

      const userDetails = localStorage.getItem('userDetails');
      if (userDetails) {
        const user = JSON.parse(userDetails);
        if (user && user.id) {
          setUserId(user.id);
          socket?.emit("register", user.id);
        }
      }
      socket.on('msgDataSet', (dataSet) => {
        if (dataSet?.data?.chatResponse) {
          setResponse(dataSet.data.chatResponse);
        }
        if (dataSet?.data?.prompt) {
          setTranscript(dataSet.data.prompt);
        }
      });
    }
    getTokenDetails();
  },[]);

  const getTokenDetails = async () => {
    const response = await getApiCall('interview-app/interview/detailkey/web');
    if (response?.status && response?.result) {
      if (response?.result?.token) {
        setTokenDetails(response.result.token);
      }
      if (response?.result?.token2) {
        setToken2Details(response.result.token2);
      }
    }
  };

  // Initialize session config
  useEffect(() => {
    if (sessionConfig) {
      console.log('Initializing session config:', sessionConfig);
      setCurrentSessionConfig({
        ...sessionConfig,
        // Use jobTitle from sessionConfig or fallback to role
        role: sessionConfig.jobTitle || 'Software Engineer',
        // Convert skillset to skills array
        skills: sessionConfig.skillset || "",
        // Default to technical interview if not specified
        interviewType: sessionConfig.interviewType || 'technical'
      });
    }
  }, [sessionConfig]);

  // Session timer state - use remainingTime from API if available (in seconds)
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(remainingTime); // No default time
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const WARNING_TIME = 60; // Show warning when 60 seconds remain

  // Effect to log the initial remaining time for debugging
  useEffect(() => {
    console.log('SpeechToText component received remainingTime:', remainingTime, 'seconds');
    
    // If remaining time is zero or negative, show payment dialog immediately
    if (remainingTime <= 0) {
      setShowPaymentDialog(true);
      setSessionExpired(true);
    }
  }, [remainingTime]);

  // Session timer reference
  const sessionTimerRef = useRef(null);

  // Speech recognition and text area references
  const recognitionRef = useRef(null);
  const transcriptAreaRef = useRef(null);
  const responseAreaRef = useRef(null);

  // Format session time remaining (MM:SS)
  const formatSessionTime = useCallback((totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Effect to handle session expiration
  useEffect(() => {
    if (sessionExpired) {
      // Call onEndSession with true to indicate time expiration
      onEndSession(true);
    }
  }, [sessionExpired, onEndSession]);

  // Start the session timer with the remaining time from API
  const startSessionTimer = useCallback(() => {
    // Check if there's any remaining time
    if (remainingTime <= 0) {
      setSessionExpired(true);
      setShowPaymentDialog(true);
      return;
    }
    
    // Use the remaining time from API
    setSessionTimeRemaining(remainingTime);
    setSessionExpired(false);

    console.log('Starting session timer with remaining time:', remainingTime, 'seconds');

    // Clear any existing interval
    if (sessionTimerRef.current) {
      clearInterval(sessionTimerRef.current);
    }

    // Start a new interval that counts down
    sessionTimerRef.current = setInterval(() => {
      setSessionTimeRemaining(prev => {
        const newTime = prev - 1;

        // Show payment dialog when 1 minute remains
        if (newTime === WARNING_TIME) {
          setShowPaymentDialog(true);
        }

        // Session expired
        if (newTime <= 0) {
          clearInterval(sessionTimerRef.current);
          sessionTimerRef.current = null;
          setSessionExpired(true);
          return 0;
        }

        return newTime;
      });
    }, 1000);
  }, [remainingTime, WARNING_TIME]);

  // Handle payment dialog close
  const handlePaymentDialogClose = useCallback(() => {
    setShowPaymentDialog(false);
  }, []);

  // Handle payment confirmation - open payment page in a new tab
  const handlePaymentConfirm = useCallback(() => {
    // Close the dialog
    setShowPaymentDialog(false);
    
    // Open payment page in a new tab
    window.open('/checkout', '_blank');
  }, []);

  // Add a function to open the payment page in a new tab
  const openPaymentPage = useCallback(() => {
    window.open('/checkout/9b1deb4d-3b7d-9bdd-4bad-2b0d7b3dcb8d', '_blank');
  }, []);

  // Start session timer on component mount
  useEffect(() => {
    console.log('Received remaining time from API:', remainingTime);
    startSessionTimer();

    // Clean up session timer on unmount
    return () => {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
    };
  }, [startSessionTimer, remainingTime]);

  useEffect(() => {
    // Initialize speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.interimResults = true;
      recognitionRef.current.continuous = true;

      let finalTranscript = "";

      recognitionRef.current.onresult = (event) => {
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + " ";
          } else {
            interimTranscript += transcript;
          }
        }

        setTranscript(finalTranscript + interimTranscript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error", event.error);
        alert("Error occurred: " + event.error);
        setIsListening(false);
      };
    } else {
      alert("Your browser doesn't support speech recognition. Try Chrome or Edge.");
    }

    // Cleanup function
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    // Auto-scroll transcript area when content changes
    if (transcriptAreaRef.current) {
      transcriptAreaRef.current.scrollTop = transcriptAreaRef.current.scrollHeight;
    }
  }, [transcript]);

  useEffect(() => {
    // Auto-scroll response area when content changes
    if (responseAreaRef.current) {
      responseAreaRef.current.scrollTop = responseAreaRef.current.scrollHeight;
    }
  }, [response]);

  const startListening = () => {
    if (!isListening && !sessionExpired) {
      try {
        // Clear the transcript when starting a new recording
        setTranscript('');

        // Stop any existing recognition
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
          } catch (e) {
            // Ignore errors when stopping
          }
        }

        // Create a completely new recognition instance
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.lang = 'en-US';
        recognitionRef.current.interimResults = true;
        recognitionRef.current.continuous = true;

        // Set up event handlers with a fresh finalTranscript
        let finalTranscript = "";

        recognitionRef.current.onresult = (event) => {
          let interimTranscript = "";

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + " ";
            } else {
              interimTranscript += transcript;
            }
          }

          // Only update with the transcript from this recognition instance
          setTranscript(finalTranscript + interimTranscript);
        };

        recognitionRef.current.onerror = (event) => {
          console.error("Speech recognition error", event.error);
          setIsListening(false);
        };

        // Start the new recognition instance
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error("Speech recognition error:", error);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
        setIsListening(false);
        // Auto-send to GPT if transcript is not empty, not loading, and session is not expired
        if (transcript.trim() && !isLoading && !sessionExpired) {
          sendToGPT();
        }
      } catch (error) {
        console.error("Speech recognition error:", error);
      }
    }
  };

  const getChatGPT = async (token, systemPrompt, prompt) => {
    let response = null;
    try {
      response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          'text': 'texting'
        },
        body: JSON.stringify({
          model: "gpt-4.1-nano",
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: prompt }
          ],
          stream: true
        })
      });
    } catch (error) {
      
    }
    return response;
  } 

  const sendToGPT = async () => {
    const apiKey = env.OPENAI_API_KEY;
    const userText = transcript.trim();

    console.log("Environment:", process.env.NODE_ENV);
    console.log("API Key available:", apiKey ? "Yes" : "No");

    if (!apiKey) {
      alert("Please add your OpenAI API key to the .env file as REACT_APP_OPENAI_API_KEY and restart the development server.");
      return;
    }

    if (!userText) {
      alert("Please record or enter some text to send to GPT.");
      return;
    }

    let skills = currentSessionConfig?.skills;
    if (typeof skills !== 'string' && Array.isArray(skills)) {
      skills = skills.split(',');
    }
    // ---- THIS IS THE KEY PART: SYSTEM PROMPT WITH CONTEXT ----
    const systemPrompt = `
You are an expert AI interviewer for the role of ${currentSessionConfig?.jobTitle || currentSessionConfig?.role || 'Software Engineer'}.
The candidate's skills: ${skills || 'N/A'}.
This is a ${currentSessionConfig?.interviewType || 'technical'} interview.
${currentSessionConfig?.jobDescription ? `Job Description: ${currentSessionConfig.jobDescription}` : ''}
Always interpret the user's question in this context. If speech input is unclear, use your understanding of the role and skills to infer the intended question and provide a clear, relevant answer.
Give the answer in 5 lines or less, with a definition and a simple example.
`;

    const prompt = userText;

    setIsLoading(true);
    setResponse('');

    try {
      let response = null;
      let tokenResult = await getChatGPT(tokenDetails, systemPrompt, prompt);
      if (tokenResult) {
        response = tokenResult;
      } else {
        response = await getChatGPT(token2Details, systemPrompt, prompt);
      }

      if (!response?.ok || !response?.body) throw new Error("Failed to stream response.");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let result = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n").filter(line => line.trim().startsWith("data:"));

        for (const line of lines) {
          const data = line.replace(/^data: /, '');
          if (data === "[DONE]") break;

          try {
            const json = JSON.parse(data);
            const content = json.choices?.[0]?.delta?.content;
            if (content) {
              result += content;
              setResponse(result);

              if (socketObj) {
                socketObj.emit('sendMessage', {
                  chatResponse: result,
                  prompt: prompt,
                  userId: userId
                });
              }
            }
          } catch (e) {
            console.error("Error parsing JSON:", e);
          }
        }
      }
    } catch (error) {
      console.error("Streaming Error:", error);
      setResponse("Error occurred: " + error.message);
    } finally {
      // Do NOT call stopListening here to avoid recursion/duplicate responses
      setIsLoading(false);
      // Clear the transcript after getting the answer
      setTranscript('');
    }
  };



  // Calculate token usage based on transcript and responses
  const calculateTokenUsage = useCallback(() => {
    // Direct calculation based on transcript and response
    // Instead of trying to find DOM elements which might not be reliable
    
    // Count words in transcript and response
    const transcriptWords = transcript ? transcript.split(/\s+/).filter(word => word.length > 0).length : 0;
    const responseWords = response ? response.split(/\s+/).filter(word => word.length > 0).length : 0;
    
    // Total word count
    const totalWords = transcriptWords + responseWords;
    
    // Estimate tokens (approximately 1.3 tokens per word for GPT models)
    const estimatedTokens = Math.ceil(totalWords * 1.3);
    
    // Add a base cost for the system prompt (approximately 20 tokens)
    const systemPromptTokens = 20;
    
    // Ensure we return at least 20 tokens even for empty conversations
    const totalTokens = Math.max(estimatedTokens + systemPromptTokens, 20);
    
    console.log('Token calculation:', {
      transcriptWords,
      responseWords,
      totalWords,
      estimatedTokens,
      systemPromptTokens,
      totalTokens
    });
    
    return totalTokens;
  }, [transcript, response]);

  // Update session config with token usage before ending session
  const handleExit = useCallback(() => {
    // Ask for confirmation before exiting
    if (window.confirm("Are you sure you want to exit? This will end your current session.")) {
      // Calculate token usage based on conversation
      const totalTokens = calculateTokenUsage();
      console.log('Estimated token usage on exit:', totalTokens);
      
      // Update session config with token usage
      if (sessionConfig) {
        sessionConfig.tokenUsage = totalTokens;
      }
      
      // Call parent's end session handler with false (not time expired)
      onEndSession(false);
    }
  }, [onEndSession, sessionConfig, calculateTokenUsage]);

  // Time expiration handler
  useEffect(() => {
    if (sessionTimeRemaining <= 0 && !sessionExpired) {
      setSessionExpired(true);
      // Notify parent component about time expiration
      onEndSession(true);
    }
  }, [sessionTimeRemaining, sessionExpired, onEndSession]);

  // Handle end session button click
  // eslint-disable-next-line
  const handleEndSession = useCallback(() => {
    if (window.confirm("Are you sure you want to end this interview session?")) {
      // Calculate token usage based on transcript and responses
      const totalTokens = calculateTokenUsage();
      
      // Update session config with token usage
      if (sessionConfig) {
        sessionConfig.tokenUsage = totalTokens;
      }
      
      // Call parent's end session handler with false (not time expired)
      onEndSession(false);
    }
  }, [onEndSession, sessionConfig, calculateTokenUsage]);

  // Add this function to handle key press events in the textarea
  const handleKeyPress = (e) => {
    // Check if Enter key is pressed without Shift key (for new line)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent default behavior (new line)
      
      // Only send if there's text and not already loading
      if (transcript.trim() && !isLoading && !sessionExpired) {
        sendToGPT();
      }
    }
  };

  const handleCloseExitDialog = () => {
    setExitDialog(false);
  };
  const endSession = () => {
    const totalTokens = calculateTokenUsage();
      console.log('Estimated token usage on exit:', totalTokens);
      
      // Update session config with token usage
      if (sessionConfig) {
        sessionConfig.tokenUsage = totalTokens;
      }
      
      // Call parent's end session handler with false (not time expired)
      onEndSession(false);
  }

  return (
    <div className="speech-to-text">
      {/* Exit button in top right corner */}
      <button className="exit-button" onClick={() => setExitDialog(true)} title="Exit">
        <CloseIcon />
      </button>

      {/* Make Payment button with icon */}
      <button className="make-payment-button" onClick={openPaymentPage} title="Purchase more time">
        <PaymentIcon style={{ marginRight: '5px', fontSize: '16px' }} />
        Pay
      </button>

      {/* Session timer display at the top */}
      {interView !== "desktopApp" && (
        <div className="timer-container">
          <AccessTimeIcon className="timer-icon" />
          <span className="timer-display session-timer">
            {formatSessionTime(sessionTimeRemaining)}
          </span>
          {isListening && <span className="listening-indicator">Recording...</span>}
          {process.env.NODE_ENV === 'development' && (
            <span className="debug-info" style={{ fontSize: '10px', marginLeft: '5px', color: '#999' }}>
              ({Math.floor(sessionTimeRemaining / 60)} min)
            </span>
          )}
        </div>
      )}

      <div className="flex-container">
        {interView === "desktopApp" && (
          <>
            <div className="answer-container">
              <div className="section-header">
                <h3>Interview Context</h3>
              </div>
            </div>
            <div className="question-container">
              <div className="context-details">
                <p><strong>Job Title:</strong> {currentSessionConfig?.jobTitle || 'N/A'}</p>
                <p><strong>Interview Time:</strong> {currentSessionConfig?.interViewTime || '0'} {currentSessionConfig?.interViewTime > 1 ? 'Minutes' : 'Minute'}</p>
                <p><strong>Skills:</strong> {currentSessionConfig?.skills || 'N/A'}</p>
                {currentSessionConfig?.jobDescription && (
                  <p><strong>Description:</strong> {currentSessionConfig.jobDescription}</p>
                )}
                {desktopToken && (
                  <>
                  <div className='row'>
                    <div className='col'>
                      <strong>Token:</strong>
                    </div>
                    <div className='col-2' style={{ textAlign: 'right' }}>
                      <Button
                        variant="contained" 
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(desktopToken);
                          setCopyToken(true);
                          setTimeout(() => setCopyToken(false), 5000);
                        }}
                        className="copy-button"
                      >
                        {copyToken && (
                          <>
                            <CheckIcon style={{ marginRight: 4 }} /> Copyd!
                          </>
                        )}
                        {!copyToken && (
                          <>
                            <ContentCopyIcon style={{ marginRight: 4 }} /> Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                    <p></p>
                    <code>
                      {desktopToken}
                    </code>
                  </>
                )}
              </div>
            </div>
          </>
        )}
        {interView === "webApp" && (
        <>
        {/* Answer section - maximized without clear button */}
        <div className="answer-container">
          <div className="section-header">
            <h3>AI Response</h3>
          </div>
          <textarea
            ref={responseAreaRef}
            className="response-area"
            value={response}
            readOnly
            placeholder="AI answer will appear here..."
            disabled={sessionExpired}
          />
        </div>
            <span style={{
              fontSize: 15,
              color: '#666',
              textAlign: 'center',
              lineHeight: '1.5'
            }}>
              (Fix the keyword <b>ONLY</b> if incorrect, then click the button or press <b>Enter</b>.)
            </span>
        {/* Question section - smaller with centered controls */}
        <div className="question-container">
          <textarea
            ref={transcriptAreaRef}
            className="transcript-area"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={sessionExpired ? "Session expired. Please make a payment to continue." : "Type or record your question here..."}
            disabled={sessionExpired}
          />

          <div className="control-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 24 }}>
            <span>Tip: Hit the button to start listening.</span>
            <button
              className={`mic-button ${isListening ? 'listening' : ''}`}
              onClick={isListening ? stopListening : startListening}
              title={isListening ? "Stop Recording" : "Start Recording"}
              disabled={sessionExpired}
              style={{ marginBottom: 16 }}
            >
              {isListening ? <StopIcon /> : <MicIcon />}
            </button>
            <span> Hit it again to stop and get a response.</span>
          </div>
        </div>
        </>
        )}
      </div>

      {/* Payment Dialog */}
      <Dialog
        open={showPaymentDialog && !sessionExpired}
        onClose={handlePaymentDialogClose}
        aria-labelledby="payment-dialog-title"
        aria-describedby="payment-dialog-description"
      >
        <DialogTitle id="payment-dialog-title">
          {"Session Expiring Soon"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="payment-dialog-description">
            Your session will expire in one minute. Unlimited FREE minutes. Email info@interviewsupport-ai.com for more FREE minutes. Would you like to make a payment to extend your session?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentDialogClose} color="primary">
            Not Now
          </Button>
          <Button onClick={handlePaymentConfirm} color="primary" autoFocus>
            Make Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Session Expired Dialog */}
      <Dialog
        open={sessionExpired}
        aria-labelledby="expired-dialog-title"
        aria-describedby="expired-dialog-description"
      >
        <DialogTitle id="expired-dialog-title">
          {"Session Expired"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="expired-dialog-description">
            Your session has expired. Please make a payment to continue using the service.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExit} color="primary">
            Exit
          </Button>
          <Button onClick={handlePaymentConfirm} color="primary" autoFocus>
            Make Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={exitDialog} onClose={handleCloseExitDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Are you sure you want to exit? This will end your current session.</DialogTitle>
        <DialogActions>
          <Button onClick={handleCloseExitDialog}>Cancel</Button>
          <Button onClick={endSession} variant="contained" color="primary">
            Exit Session
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SpeechToText;






